import { type ImageMask, maskClipPath, maskPath, type Stroke } from "@cva/shared";
import type { CSSProperties, ReactNode } from "react";
import { dashArray } from "./shape";

/**
 * Clips its children to an image mask ("Bildform"). A CSS `clip-path: path()`
 * in document px: Chromium keeps it as a vector clip in PDFs (also the CMYK
 * print PDF, which only converts colours and images), and the editor,
 * thumbnails, PNG exports and video overlays render it the same way.
 */
export function MaskClip({
  mask,
  width,
  height,
  children,
}: {
  mask: ImageMask;
  width: number;
  height: number;
  children: ReactNode;
}) {
  const style: CSSProperties = {
    position: "absolute",
    inset: 0,
    clipPath: maskClipPath(mask, width, height),
  };
  return <div style={style}>{children}</div>;
}

/**
 * Stroke along the mask outline, inside the form like the border of a
 * rectangular image: the path is drawn twice as wide and clipped to itself.
 */
export function MaskStroke({
  mask,
  width,
  height,
  stroke,
}: {
  mask: ImageMask;
  width: number;
  height: number;
  stroke: Stroke;
}) {
  const d = maskPath(mask, width, height);
  const doubled = { ...stroke, width: stroke.width * 2 };
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        display: "block",
        pointerEvents: "none",
        clipPath: `path("${d}")`,
      }}
    >
      <path
        d={d}
        fill="none"
        stroke={stroke.color}
        strokeWidth={doubled.width}
        strokeDasharray={dashArray(doubled)}
        strokeLinecap={stroke.dash === "dotted" ? "round" : "butt"}
        strokeLinejoin="round"
      />
    </svg>
  );
}
